import React, { useEffect, useRef, useState } from 'react';
import { View, Text, Pressable, Platform } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import Animated, { useAnimatedStyle, useSharedValue, withTiming, withDelay } from 'react-native-reanimated';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Avatar from '@/components/People/Avatar';
import { getFile, getMediaFileInfo } from '@/utils/FileManager';
import { isToday, isYesterday } from '@/utils/dater';
import Reaction from '../Reaction';
import { Query } from '@/utils/react-query/Queries';
import Hyperlink from 'react-native-hyperlink'
import { openHyperlink } from '@/utils/linking'
import LinkPreview from '@/components/Library/LinkPreview';
import { renderTextWithMentions } from '@/utils/mentionHelper';
import { hasMentions, stripMentions } from '@/utils/mentionFormat';
import { addMessageReaction, removeMessageReaction } from '@/data/connect';
import { ActivityIndicator } from 'react-native-paper';
import useMessagesStore from '@/data/store/useMessagesStore';
import { useRouter } from 'expo-router';
import MediaViewer from '@/components/General/MediaViewer';
import Unicon from '@/components/General/Unicon';

const ChatBubble = ({ message, isCurrentUser, showSender = true, showTime = true, showNewDateLabel = false, isFirstInGroup = false, isLastInGroup = false, canInteract = true, canReact = canInteract}) => {
    const [attachments, setAttachments] = useState([])
    const [links, setLinks] = useState([])
    const [reactions, setReactions] = useState([])

    const setMessageForReaction = useMessagesStore(state => state.setMessageForReaction)
    const scrollToMessageCallback = useMessagesStore(state => state.scrollToMessageCallback)
    const highlightedMessageID = useMessagesStore(state => state.highlightedMessageID)

    const bubble_ref = useRef();

    const queryClient = useQueryClient();
    const router = useRouter();

    const addReactionMutation = useMutation({
        mutationFn: addMessageReaction,
        onMutate: (data) => {
            if(!data) return;
            queryClient.setQueryData(['messages', {conversationID: message?.conversationID}], old => {
                const newData = {...old};
                newData.pages = newData.pages.map(page => ({...page, data: page.data.map(o => o.id == data.messageID ? {...o, Reactions: [...(o.Reactions?.filter(r => !(r.messageID == data.messageID && r.personID == data.personID)) || []), {...data, Person: loggedInUser}]} : o)}))
                return newData
            });
        },
    })

    const removeReactionMutation = useMutation({
        mutationFn: removeMessageReaction,
        onMutate: (data) => {
            if(!data) return;
            queryClient.setQueryData(['messages', {conversationID: message.conversationID}], old => {
                const newData = {...old};
                newData.pages = newData.pages.map(page => ({...page, data: page.data.map(o => o.id == data.messageID ? {...o, Reactions: [...(o.Reactions?.filter(r => !(r.messageID == data.messageID && r.personID == data.personID)) || [])]} : o)}))
                return newData
            })
        }
    })

    useEffect(() => {
        if (Platform.OS !== 'web') return;
        const prevent = (e) => { e.preventDefault(); };
        document.addEventListener('contextmenu', prevent);
        return () => document.removeEventListener('contextmenu', prevent);
    }, []);

    useEffect(() => {
        if(message.Reactions && message.Reactions.length > 0 && !message.isDeleted){
            const reactionTypes = {}
            message.Reactions.forEach((r) => {
                if(!reactionTypes[r.reaction]){
                    reactionTypes[r.reaction] = []
                }

                reactionTypes[r.reaction].push(r)
            })

            setReactions(reactionTypes)
        }
        else{
            setReactions([])
        }
    }, [message])

    useEffect(() => {
        if(message.attachments && message.attachments.length != attachments.length){
            let promises = [];

            for(let i=0; i<message.attachments?.length; i++){
                if(!message.attachments[i].startsWith('http')){
                    promises[i] = getFile(message.attachments[i], true, true);
                }
                else{
                    promises[i] = {uri: message.attachments[i], };
                }
            }

            Promise.all(promises).then(async (results) => {
                const new_attachments = [];
                for(let i=0; i<results.length; i++){
                    //getFile returns false when the storage file doesn't exist
                    const uri = results[i]?.uri;
                    if(!uri) continue;
                    new_attachments[i] = await getMediaFileInfo(uri);
                }

                setAttachments(new_attachments.filter(Boolean));
            }).catch(() => {
                setAttachments([]);
            });
        }

    }, [JSON.stringify(message.attachments)])

    const { data: loggedInUser } = Query.people.loggedInUser()


    const dateHeader = !showNewDateLabel
        ? <></>
        : isToday(message.sentAt)
            ? "Today"
            : isYesterday(message.sentAt)
                ? "Yesterday"
                : new Date(message.sentAt).toLocaleString('en-US', {month:"short", day:"numeric", year:"numeric"})


    const openReactionMenu = () => {
        if(!canInteract || message.isDeleted){
            return;
        }

        setMessageForReaction({
            message,
            bubble_ref,
            isCurrentUser,
            contextMenuOpen: true,
        })
    }

    const messageReactions = Object.entries(reactions)?.map(([emojiName, list]) => {
        return <Reaction key={emojiName} emojiName={emojiName} people={list.map(r => r.Person)} onPress={() => {
            if(!canReact) return;
            const currentUserReaction = list.find(r => r.Person?.id == loggedInUser?.id)

            // Toggle off when tapping the user's existing reaction; otherwise upsert
            // (add or switch). Avoids firing remove + add in parallel, which raced
            // the unique_person_message constraint and threw duplicate-key errors.
            if(currentUserReaction?.reaction == emojiName){
                removeReactionMutation.mutate({messageID: message.id, personID: loggedInUser?.id})
            }
            else{
                addReactionMutation.mutate({messageID: message.id, reaction: emojiName, personID: loggedInUser?.id})
            }
        }}/>
    })


    const messageAttachments = (!message.isDeleted && attachments?.length > 0)
        ? <MediaViewer media={attachments} />
        : null;

    const linkComponents = links.map(l => <LinkPreview url={l} key={l} />)

    const hasReply = !!message.repliedTo;

    //Show an "Edited" label when the message has been edited (and not deleted).
    const isEdited = !!message.editedAt && !message.isDeleted;

    //Highlight fade animation — opacity animates from 0 → 1 → 0
    const highlightOpacity = useSharedValue(0);
    useEffect(() => {
        if(highlightedMessageID === message.id){
            //Fade in quickly, hold, then fade out
            highlightOpacity.value = 0;
            highlightOpacity.value = withTiming(1, { duration: 200 }, () => {
                highlightOpacity.value = withDelay(1200, withTiming(0, { duration: 800 }));
            });
        }
    }, [highlightedMessageID]);

    const highlightStyle = useAnimatedStyle(() => ({
        opacity: highlightOpacity.value * 0.15,
    }));

    return (
        <>
            {showNewDateLabel &&
                <View style={styles.newDateLabelContainer}>
                    <Text style={styles.dateHeader}>{dateHeader}</Text>
                    <View style={styles.dateHeaderLine}></View>
                </View>
            }
            <Pressable
                ref={bubble_ref}
                style={styles.messageContainer(isCurrentUser, isFirstInGroup, message.isDeleted)}
                onLongPress={Platform.OS !== 'web' ? openReactionMenu : undefined}
                onPointerDown={Platform.OS === 'web' ? (e) => {
                    const button = e?.nativeEvent?.button ?? e?.button;
                    if(button === 2){
                        openReactionMenu();
                    }
                } : undefined}
            >
                {/* Highlight overlay — fades in/out */}
                <Animated.View
                    pointerEvents="none"
                    style={[styles.highlightOverlay, highlightStyle]}
                />
                {/* When there's a reply, wrap everything in a row with a continuous vertical bar on the outside */}
                {hasReply ? (
                    <View style={styles.threadContainer(isCurrentUser)}>
                        <View style={styles.replyIconContainer}>
                            <Unicon name="reply" library="fa" size={14} colorVariant="textSecondary" /> 
                        </View>
                        <View style={styles.threadBar(isCurrentUser)} />
                        <View style={styles.threadContent}>
                            {/* Tappable reply preview */}
                            <Pressable
                                style={styles.replyPreviewContainer}
                                onPress={() => {
                                    if(scrollToMessageCallback){
                                        scrollToMessageCallback(message.repliedTo?.id);
                                    }
                                }}
                            >
                                <Text style={styles.replyPreviewName(isCurrentUser)} numberOfLines={1}>
                                    {message.repliedTo?.Sender?.firstName + " " + message.repliedTo?.Sender?.lastName}
                                </Text>
                                <Text style={styles.replyPreviewText} numberOfLines={2}>
                                    {message.repliedTo?.isDeleted
                                        ? "This message was deleted"
                                        : (message.repliedTo?.text
                                            ? stripMentions(message.repliedTo.text)
                                            : (message.repliedTo?.attachments?.length > 0 ? "Attachment" : ""))}
                                </Text>
                            </Pressable>

                            {/* Main bubble content */}
                            <View>
                                <View style={styles.senderContainer(isCurrentUser)}>
                                    <View style={styles.senderInfoRow}>
                                        {(showSender) && <Text style={styles.name}>{message.Sender?.firstName + " " + message.Sender?.lastName}</Text>}
                                        <Text style={[styles.time, {marginTop:1}]}>{new Date(message.sentAt).toLocaleString('en-US', {hour:'numeric', minute:'2-digit'})}</Text>
                                        {isEdited && <Text style={styles.editedLabel}>Edited</Text>}
                                    </View>
                                </View>

                                <View style={styles.wrapper(isCurrentUser)}>
                                    <View style={styles.avatarContainer}>
                                        {((!isCurrentUser) && showSender) && <View><Avatar size={30} profile={message.Sender} disableBorder /></View> }
                                    </View>

                                    <View style={styles.messageContentWrapper}>
                                        {!message.isDeleted && message.attachments?.length > 0 && attachments.length == 0 ? <View style={styles.loadingContainer}><ActivityIndicator /></View> : messageAttachments}

                                        { (message.isDeleted || (message.text && message.text.length > 0)) &&
                                            <>
                                                { (links.length == 0 || !links.includes(message.text)) &&
                                                    <View style={styles.bubbleContainer(isCurrentUser, isFirstInGroup, isLastInGroup)}>
                                                        {hasMentions(message.text) && !message.isDeleted
                                                            ? <Text style={styles.text(isCurrentUser, message.isDeleted)}>
                                                                {renderTextWithMentions(message.text, router)}
                                                              </Text>
                                                            : <Hyperlink linkText={ url => {
                                                                if(!links.includes(url)){
                                                                    setTimeout(() => setLinks(prev => !prev.includes(url) ? [...prev, url] : prev), 100)
                                                                }

                                                                return url
                                                            }} onPress={(url) => openHyperlink(url, router)} linkStyle={{textDecorationLine:"underline"}}>
                                                                <Text style={styles.text(isCurrentUser, message.isDeleted)}>
                                                                    {message.isDeleted ? "This message has been deleted" : message.text}
                                                                </Text>
                                                            </Hyperlink>
                                                        }
                                                    </View>
                                                }

                                                <View style={styles.linkPreviewContainer}>{linkComponents}</View>
                                            </>
                                        }

                                        {(messageReactions?.length > 0) &&
                                            <View style={styles.reactionsContainer(isCurrentUser)}>
                                                {messageReactions}
                                            </View>
                                        }
                                    </View>
                                </View>
                            </View>
                        </View>
                    </View>
                ) : (
                    <View>
                        <View style={styles.senderContainer(isCurrentUser)}>
                            <View style={styles.senderInfoRow}>
                                {(showSender) && <Text style={styles.name}>{message.Sender?.firstName + " " + message.Sender?.lastName}</Text>}
                                {(showTime) && <Text style={[styles.time, {marginTop:1}]}>{new Date(message.sentAt).toLocaleString('en-US', {hour:'numeric', minute:'2-digit'})}</Text>}
                                {isEdited && <Text style={styles.editedLabel}>Edited</Text>}
                            </View>
                        </View>

                        <View style={styles.wrapper(isCurrentUser)}>
                            <View style={styles.avatarContainer}>
                                {((!isCurrentUser) && showSender) && <View><Avatar size={30} profile={message.Sender} disableBorder /></View> }
                            </View>

                            <View style={styles.messageContentWrapper}>
                                {!message.isDeleted && message.attachments?.length > 0 && attachments.length == 0 ? <View style={styles.loadingContainer}><ActivityIndicator /></View> : messageAttachments}

                                { (message.isDeleted || (message.text && message.text.length > 0)) &&
                                    <>
                                        { (links.length == 0 || !links.includes(message.text)) &&
                                            <View style={styles.bubbleContainer(isCurrentUser, isFirstInGroup, isLastInGroup)}>
                                                {hasMentions(message.text) && !message.isDeleted
                                                    ? <Text style={styles.text(isCurrentUser, message.isDeleted)}>
                                                        {renderTextWithMentions(message.text, router)}
                                                      </Text>
                                                    : <Hyperlink linkText={ url => {
                                                        if(!links.includes(url)){
                                                            setTimeout(() => setLinks(prev => !prev.includes(url) ? [...prev, url] : prev), 100)
                                                        }

                                                        return url
                                                    }} onPress={(url) => openHyperlink(url, router)} linkStyle={{textDecorationLine:"underline"}}>
                                                        <Text style={styles.text(isCurrentUser, message.isDeleted)}>
                                                            {message.isDeleted ? "This message has been deleted" : message.text}
                                                        </Text>
                                                    </Hyperlink>
                                                }
                                            </View>
                                        }

                                        <View style={styles.linkPreviewContainer}>{linkComponents}</View>
                                    </>
                                }

                                {(messageReactions?.length > 0) &&
                                    <View style={styles.reactionsContainer(isCurrentUser)}>
                                        {messageReactions}
                                    </View>
                                }
                            </View>
                        </View>
                    </View>
                )}
            </Pressable>
        </>
    );
};


const styles = StyleSheet.create((theme, rt) => ({
    dateHeader: {
        color: theme.colors.textSecondary,
        fontSize: 12,
        zIndex: 2,
        backgroundColor: theme.colors.background,
        paddingHorizontal: 10,
        paddingVertical: 5
    },
    dateHeaderLine: {
        height: 1,
        backgroundColor: theme.colors.border,
        opacity: 0.3,
        width: rt.screen.width - 20,
        marginTop: rt.isLandscape ? -12 : -6,
        zIndex: 1
    },
    text: (isCurrentUser, isDeleted) => ({
        color: isCurrentUser ? (rt.themeName == 'dark' ? theme.colors.primary : theme.colors.primary) : theme.colors.text,
        fontStyle: !isDeleted ? "normal" : "italic"
    }),
    reactionsContainer: (isCurrentUser) => ({
        justifyContent: isCurrentUser ? "flex-end" : "flex-start",
        alignItems: "flex-end",
        marginTop: -12,
        flexDirection: "row",
    }),
    messageContainer: (isCurrentUser, isFirstInGroup, isDeleted) => ({
        maxWidth: '85%',
        marginTop: isFirstInGroup ? 12 : 1,
        alignSelf: isCurrentUser ? 'flex-end' : 'flex-start',
        paddingHorizontal: 10,
        opacity: isDeleted ? 0.5 : 1
    }),
    highlightOverlay: {
        position: 'absolute',
        top: -4,
        bottom: -4,
        left: -4,
        right: -4,
        backgroundColor: theme.colors.primary,
        borderRadius: 12,
        marginVertical: 5,
    },
    //Thread container — horizontal row: bar on the outside edge, content inside.
    //Current user: bar on right (row-reverse). Others: bar on left (row).
    threadContainer: (isCurrentUser) => ({
        flexDirection: isCurrentUser ? 'row-reverse' : 'row',
        alignSelf: isCurrentUser ? 'flex-end' : 'flex-start',
    }),
    threadBar: (isCurrentUser) => ({
        width: 3,
        borderRadius: 1.5,
        backgroundColor: theme.colors.textSecondary,
        marginLeft: isCurrentUser ? 4 : 8,
        marginRight: isCurrentUser ? 8 : 4,
        opacity: 0.4,
    }),
    threadContent: {
        flexShrink: 1,
    },
    //Reply preview — compact card, tappable to scroll to original
    replyIconContainer: {
        position: 'absolute',
        top: -12,
        left: -12,
        height: 24,
        width: 24,
        backgroundColor: rt.themeName == 'dark' ? theme.colors.surfaceVariant : theme.colors.border,
        borderRadius: 24,
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1,
    },
    replyPreviewContainer: {
        backgroundColor: rt.themeName == 'dark' ? theme.colors.surfaceVariant : theme.colors.border,
        borderRadius: 8,
        paddingHorizontal: 10,
        paddingVertical: 6,
        marginBottom: 6,
        opacity: 0.7,
        maxWidth: '100%',
    },
    replyPreviewName: (isCurrentUser) => ({
        fontSize: 12,
        fontFamily: theme.fonts.bold,
        color: isCurrentUser ? (rt.themeName == 'dark' ? theme.colors.primary : theme.colors.primary) : theme.colors.primary,
        marginBottom: 1,
    }),
    replyPreviewText: {
        fontSize: 12,
        color: theme.colors.textSecondary,
        lineHeight: 16,
    },
    bubbleContainer: (isCurrentUser, isFirstInGroup, isLastInGroup) => ({
        paddingTop: 8,
        paddingBottom: 8,
        paddingHorizontal: 14,
        borderRadius: 18,
        //Flatten the corner on the sender's side when this bubble connects to an adjacent one.
        borderTopLeftRadius: !isCurrentUser && !isFirstInGroup ? 6 : 18,
        borderTopRightRadius: isCurrentUser && !isFirstInGroup ? 6 : 18,
        borderBottomLeftRadius: !isCurrentUser && !isLastInGroup ? 6 : 18,
        borderBottomRightRadius: isCurrentUser && !isLastInGroup ? 6 : 18,
        alignSelf: isCurrentUser ? 'flex-end' : 'flex-start',
        backgroundColor: isCurrentUser
            ? (rt.themeName == 'dark' ? theme.colors.onPrimary : '#DCE4FB')
            : (rt.themeName == 'dark' ? theme.colors.surfaceVariant : theme.colors.border),
    }),
    wrapper: (isCurrentUser) => ({
        flexDirection: "row",
        alignSelf: isCurrentUser ? 'flex-end' : 'flex-start',
    }),
    senderContainer: (isCurrentUser) => ({
        alignSelf: isCurrentUser ? 'flex-end' : 'flex-start',
        paddingBottom: 3,
    }),
    name: {
        fontFamily: theme.fonts.heading,
        fontSize: 12,
        color: theme.colors.text,
        paddingRight: 10
    },
    time: {
        fontSize: 10,
        color: theme.colors.textSecondary,
        fontStyle: "italic"
    },
    editedLabel: {
        fontSize: 10,
        color: theme.colors.textSecondary,
        fontStyle: "italic",
        marginLeft: 4,
    },
    attachmentButton: (isCurrentUser, width, height) => ({
        borderRadius: 8,
        overflow: "hidden",
        marginVertical: 5,
        width: width || 250,
        height: height || 250,
        alignSelf: isCurrentUser ? "flex-end" : "flex-start"
    }),
    attachmentImage: {
        width: "100%",
        height: "100%"
    },
    newDateLabelContainer: {
        width: "100%",
        alignItems: "center",
        paddingVertical: 5,
        marginVertical: 10
    },
    senderInfoRow: {
        flexDirection: "row",
        alignItems: "center",
        marginLeft: 45
    },
    avatarContainer: {
        width: 30,
        marginRight: 10
    },
    messageContentWrapper: {
        maxWidth: Math.min(rt.screen.width - 100, 500)
    },
    loadingContainer: {
        height: 250,
        width: 100,
        justifyContent: "center",
        alignItems: "center"
    },
    linkPreviewContainer: {
        width: "100%",
        alignItems: "flex-end",
        marginTop: 5
    }
}));

export default React.memo(ChatBubble);
